/**
 * TypeScript mirror of `App\Enums\DhRejectionReason` in aria-api.
 *
 * The POS reject-reason picker reads from this constant directly
 * (no network round-trip per render). The PHP enum is the source of
 * truth; this file must stay in sync. A parity script
 * (`pnpm run check:dh-reasons`) diffs this constant against the
 * `/api/delivery/glovo/rejection-reasons` manifest endpoint; the CI
 * `checks.yml` app-core step runs it against the L3 staging API.
 */
/**
 * All 24 DH rejection reasons, in spec documentation order.
 * Applicability metadata mirrors the PHP APPLICABILITY map exactly.
 */
export const DH_REJECTION_REASONS = Object.freeze([
    { code: 'ITEM_UNAVAILABLE', types: ['own_delivery', 'vendor_delivery', 'pickup'], timing: ['before_acceptance'] },
    { code: 'CLOSED', types: ['own_delivery', 'vendor_delivery', 'pickup'], timing: ['before_acceptance'] },
    { code: 'TOO_BUSY', types: ['own_delivery', 'vendor_delivery', 'pickup'], timing: ['before_acceptance'] },
    { code: 'NO_PICKER', types: ['own_delivery', 'vendor_delivery', 'pickup'], timing: ['before_acceptance'] },
    { code: 'NO_RESPONSE', types: ['own_delivery', 'vendor_delivery', 'pickup'], timing: ['before_acceptance'] },
    { code: 'OUTSIDE_DELIVERY_AREA', types: ['own_delivery', 'vendor_delivery'], timing: ['before_acceptance', 'after_acceptance'] },
    { code: 'ADDRESS_INCOMPLETE_MISSTATED', types: ['vendor_delivery'], timing: ['after_acceptance'] },
    { code: 'UNABLE_TO_FIND', types: ['vendor_delivery'], timing: ['after_acceptance'] },
    { code: 'BAD_WEATHER', types: ['own_delivery', 'vendor_delivery'], timing: ['before_acceptance', 'after_acceptance'] },
    { code: 'BLACKLISTED', types: ['own_delivery', 'vendor_delivery', 'pickup'], timing: ['before_acceptance'] },
    { code: 'CARD_READER_NOT_AVAILABLE', types: ['vendor_delivery', 'pickup'], timing: ['before_acceptance', 'after_acceptance'] },
    { code: 'UNABLE_TO_PAY', types: ['vendor_delivery', 'pickup'], timing: ['after_acceptance'] },
    { code: 'FRAUD_PRANK', types: ['own_delivery', 'vendor_delivery', 'pickup'], timing: ['before_acceptance'] },
    { code: 'TEST_ORDER', types: ['own_delivery', 'vendor_delivery', 'pickup'], timing: ['before_acceptance'] },
    { code: 'MENU_ACCOUNT_SETTINGS', types: ['own_delivery', 'vendor_delivery', 'pickup'], timing: ['before_acceptance'] },
    { code: 'CONTENT_WRONG_MISLEADING', types: ['vendor_delivery', 'pickup'], timing: ['after_acceptance'] },
    { code: 'FOOD_QUALITY_SPILLAGE', types: ['vendor_delivery', 'pickup'], timing: ['after_acceptance'] },
    { code: 'LATE_DELIVERY', types: ['vendor_delivery', 'pickup'], timing: ['after_acceptance'] },
    { code: 'WRONG_ORDER_ITEMS_DELIVERED', types: ['vendor_delivery'], timing: ['after_acceptance'] },
    { code: 'WILL_NOT_WORK_WITH_PLATFORM', types: ['own_delivery', 'vendor_delivery', 'pickup'], timing: ['before_acceptance'] },
    { code: 'MOV_NOT_REACHED', types: ['talabat_only'], timing: ['before_acceptance'] },
    { code: 'NO_COURIER', types: ['vendor_delivery'], timing: ['before_acceptance'] },
    { code: 'UNPROFESSIONAL_BEHAVIOUR', types: ['own_delivery', 'vendor_delivery', 'pickup'], timing: ['after_acceptance'] },
    { code: 'TECHNICAL_PROBLEM', types: ['own_delivery', 'vendor_delivery', 'pickup'], timing: ['before_acceptance'] },
]);
/**
 * Filter reasons valid for the given expedition type + lifecycle timing.
 * The POS reject-reason picker calls this to populate its dropdown for
 * the ticket the merchant is rejecting.
 *
 * Timing is derived FE-side from the ticket's current status:
 *   pre-'ready' statuses → 'before_acceptance'
 *   'ready' onwards      → 'after_acceptance'
 */
export function validReasonsFor(expeditionType, timing) {
    if (!expeditionType)
        return [];
    return DH_REJECTION_REASONS.filter(r => r.types.includes(expeditionType) && r.timing.includes(timing));
}
export function isValidDhRejectionCode(code) {
    if (!code)
        return false;
    return DH_REJECTION_REASONS.some(r => r.code === code);
}
/**
 * Translation key path under packages/app-resources/src/locales/i18n/{locale}.json.
 * Use with vue-i18n: `t(rejectionReasonLabelKey('ITEM_UNAVAILABLE'))`.
 */
export function rejectionReasonLabelKey(code) {
    return `delivery.glovo.rejectionReasons.${code}`;
}
//# sourceMappingURL=DhRejectionReasons.js.map